import * as api from '../api/client';

/**
 * Browser push from the client's side: register the service worker,
 * subscribe with the server's VAPID key, and keep the server's list of
 * devices in step. Everything here degrades to "unsupported" rather than
 * throwing, so the Profile page can explain instead of crash.
 */
export type PushSupport = 'supported' | 'insecure-context' | 'unsupported';

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
  // Service workers need HTTPS (or localhost).
  if (!window.isSecureContext) return 'insecure-context';
  return 'supported';
}

export function permissionState(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (pushSupport() !== 'supported') return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    return null;
  }
}

/** The subscription this browser currently holds, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** Ask permission, subscribe with the server's key and register the device. */
export async function subscribe(): Promise<PushSubscription> {
  const reg = await registerServiceWorker();
  if (!reg) throw new Error('Browser notifications are not supported here');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted');
  const { public_key } = await api.getVapidPublicKey();
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(public_key) as BufferSource,
  });
  await api.subscribeBrowserPush(sub.toJSON());
  return sub;
}

/** Forget the device on the server, then in the browser. */
export async function unsubscribe(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  try { await api.unsubscribeBrowserPush(sub.endpoint); } catch { /* already gone server-side */ }
  await sub.unsubscribe();
}
